import posthog from 'posthog-js';

/**
 * Advanced Tracking Utilities for PostHog
 * Includes: Heartbeat events, Tab visibility tracking, Scroll depth tracking
 */

// Configuration
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 90, 100]; // Percentage thresholds

// Track active heartbeat intervals
const activeHeartbeats = new Map<string, NodeJS.Timeout>();
const scrollDepthTracked = new Map<string, Set<number>>();

/**
 * Start heartbeat tracking for a page
 * Sends periodic events while user is active on the page
 */
export const startHeartbeat = (
  pageName: string,
  properties?: Record<string, any>
) => {
  // Don't start if PostHog isn't loaded
  if (!posthog.__loaded) return;

  // Clear existing heartbeat for this page if any
  stopHeartbeat(pageName);

  let heartbeatCount = 0;
  const startTime = Date.now();

  const sendHeartbeat = () => {
    // Only send if tab is visible
    if (document.hidden) return;

    heartbeatCount++;
    const timeElapsed = Math.floor((Date.now() - startTime) / 1000); // seconds

    posthog.capture('page_heartbeat', {
      page_name: pageName,
      heartbeat_count: heartbeatCount,
      time_elapsed_seconds: timeElapsed,
      is_active: true,
      ...properties,
    });
  };

  // Send initial heartbeat
  sendHeartbeat();

  // Set up interval
  const intervalId = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
  activeHeartbeats.set(pageName, intervalId);
};

/**
 * Stop heartbeat tracking for a page
 */
export const stopHeartbeat = (pageName: string) => {
  const intervalId = activeHeartbeats.get(pageName);
  if (intervalId) {
    clearInterval(intervalId);
    activeHeartbeats.delete(pageName);
  }
};

/**
 * Stop all active heartbeats
 */
export const stopAllHeartbeats = () => {
  activeHeartbeats.forEach((intervalId) => clearInterval(intervalId));
  activeHeartbeats.clear();
};

/**
 * Track tab visibility changes
 * Automatically pauses/resumes tracking when user switches tabs
 */
export const initTabVisibilityTracking = () => {
  if (!posthog.__loaded) return;

  let lastVisibilityChange = Date.now();
  let wasHidden = document.hidden;

  const handleVisibilityChange = () => {
    const now = Date.now();
    const timeAway = wasHidden ? Math.floor((now - lastVisibilityChange) / 1000) : 0;

    posthog.capture('tab_visibility_change', {
      is_visible: !document.hidden,
      was_hidden: wasHidden,
      time_away_seconds: timeAway,
      page_url: window.location.pathname,
    });

    wasHidden = document.hidden;
    lastVisibilityChange = now;
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

/**
 * Track scroll depth on a page
 * Fires events when user reaches certain scroll thresholds
 */
export const initScrollDepthTracking = (
  pageName: string,
  properties?: Record<string, any>
) => {
  if (!posthog.__loaded) return;

  // Initialize tracking set for this page
  if (!scrollDepthTracked.has(pageName)) {
    scrollDepthTracked.set(pageName, new Set());
  }

  const trackedDepths = scrollDepthTracked.get(pageName)!;
  let maxDepthReached = 0;

  const handleScroll = () => {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    
    const scrollPercentage = Math.floor(
      ((scrollTop + windowHeight) / documentHeight) * 100
    );

    // Update max depth
    if (scrollPercentage > maxDepthReached) {
      maxDepthReached = scrollPercentage;
    }

    // Check thresholds
    SCROLL_DEPTH_THRESHOLDS.forEach((threshold) => {
      if (scrollPercentage >= threshold && !trackedDepths.has(threshold)) {
        trackedDepths.add(threshold);
        
        posthog.capture('scroll_depth_reached', {
          page_name: pageName,
          depth_percentage: threshold,
          max_depth: maxDepthReached,
          ...properties,
        });
      }
    });
  };

  // Throttle scroll events
  let scrollTimeout: NodeJS.Timeout;
  const throttledScroll = () => {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(handleScroll, 100);
  };

  window.addEventListener('scroll', throttledScroll, { passive: true });

  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', throttledScroll);
    scrollDepthTracked.delete(pageName);
  };
};

/**
 * Track time spent on page with enhanced accuracy
 * Combines heartbeat and visibility tracking
 */
export const trackPageTimeStart = (
  pageName: string,
  properties?: Record<string, any>
) => {
  const startTime = Date.now();
  let totalActiveTime = 0;
  let lastActiveTime = startTime;
  let isActive = !document.hidden;

  // Start heartbeat
  startHeartbeat(pageName, properties);

  // Track visibility changes
  const handleVisibilityChange = () => {
    const now = Date.now();
    
    if (document.hidden) {
      // Tab became hidden - add to active time
      if (isActive) {
        totalActiveTime += now - lastActiveTime;
      }
      isActive = false;
    } else {
      // Tab became visible - reset timer
      isActive = true;
      lastActiveTime = now;
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return function to stop tracking and get final time
  return () => {
    const now = Date.now();
    
    // Add final active time if tab is visible
    if (isActive) {
      totalActiveTime += now - lastActiveTime;
    }

    stopHeartbeat(pageName);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    const totalTime = Math.floor((now - startTime) / 1000);
    const activeTime = Math.floor(totalActiveTime / 1000);

    posthog.capture('page_time_complete', {
      page_name: pageName,
      total_time_seconds: totalTime,
      active_time_seconds: activeTime,
      idle_time_seconds: totalTime - activeTime,
      ...properties,
    });

    return {
      totalTime,
      activeTime,
      idleTime: totalTime - activeTime,
    };
  };
};

/**
 * Initialize all advanced tracking for a page
 * Call this in useEffect on page mount
 */
export const initPageTracking = (
  pageName: string,
  options: {
    enableHeartbeat?: boolean;
    enableScrollDepth?: boolean;
    properties?: Record<string, any>;
  } = {}
) => {
  const {
    enableHeartbeat = true,
    enableScrollDepth = true,
    properties = {},
  } = options;

  const cleanupFunctions: (() => void)[] = [];

  // Start heartbeat if enabled
  if (enableHeartbeat) {
    startHeartbeat(pageName, properties);
    cleanupFunctions.push(() => stopHeartbeat(pageName));
  }

  // Start scroll depth tracking if enabled
  if (enableScrollDepth) {
    const cleanup = initScrollDepthTracking(pageName, properties);
    if (cleanup) cleanupFunctions.push(cleanup);
  }

  // Return combined cleanup function
  return () => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  };
};
